import { gql, useQuery } from "@apollo/client";
import type { Metaobject, MetaobjectEdge } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IMetaobjectQuery } from "@repo/shopify/queries/metaobject-gql";
import { metaobjectQuery } from "@repo/shopify/queries/metaobject-gql";

import Button from "@atoms/Button";
import Loading from "@atoms/Loading";
import Select from "@atoms/Select";

import RecruitDetailContent from "@pages/Recruit/RecruitDetailContent";
import RecruitLayout from "@pages/Recruit/RecruitLayout";

import type { ISortOption } from "@const/constant";
import { SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";

import useHeaderScroll from "@hooks/useHeaderScroll";

import { useLayoutStore } from "@store/layout";

const REGION_OPTIONS = [
  { name: "All Stores", value: "all" },
  { name: "Tokyo", value: "Tokyo" },
  { name: "Osaka", value: "Osaka" },
];

export default function Recruit() {
  const { openToast, setMeta, showFooter } = useLayoutStore();
  const { isPC } = useCheckDevice();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort") || "new";

  const [sort, setSort] = useState<ISortOption>(sortParam === "old" ? SORT[0] : SORT[1]);
  const [region, setRegion] = useState(REGION_OPTIONS[0]);
  const [selectedHandle, setSelectedHandle] = useState<string>();

  const { data, loading } = useQuery<IMetaobjectQuery>(gql(metaobjectQuery.query), {
    variables: { type: "recruit", first: 50 },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setMeta({ title: `Recruit - ${DEFAULT_TITLE}` });
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const getValue = (node: Metaobject, key: string) => {
    return node.fields.find((item) => item.key === key);
  };

  const recruits = useMemo(() => {
    const edges = data?.metaobjects.edges || [];
    return [...edges]
      .filter(({ node }: MetaobjectEdge) => {
        if (region.value === "all") return true;
        return getValue(node, "region")?.value === region.value;
      })
      .sort(({ node: a }: MetaobjectEdge, { node: b }: MetaobjectEdge) => {
        const dateA = getValue(a, "date")?.value || "";
        const dateB = getValue(b, "date")?.value || "";
        return sort.reverse ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
      });
  }, [data, sort, region]);

  useHeaderScroll();

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-12 pc:gap-y-0 col-span-8 grid grid-cols-subgrid gap-y-2.5">
          <section
            className="pc:col-span-2 pc:top-30 sticky top-0 z-10 col-span-8 grid h-fit grid-cols-subgrid flex-col gap-y-1"
            id="filter"
          >
            {isPC ? (
              <div className="col-span-2 grid h-fit grid-cols-subgrid leading-none">
                {/* Sort */}
                <Button
                  as="text"
                  className="whitespace-nowrap"
                  onClick={() => {
                    const sortIndex = SORT.findIndex((option) => option.label === sort.label);
                    setSort(SORT[sortIndex === 0 ? 1 : 0]);
                    setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
                  }}
                >
                  {sort.label}
                </Button>
                {/* Region Menu*/}
                <div className="flex flex-col gap-1.5">
                  {REGION_OPTIONS.map((option) => (
                    <Button
                      as="text"
                      isActive={region.value === option.value}
                      key={option.value}
                      onClick={() => {
                        setRegion(option);
                      }}
                    >
                      {option.name}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="pc:hidden col-span-8 flex items-center justify-between gap-2 bg-white py-2.5">
                {/* Region Menu*/}
                <div className="flex items-center gap-1">
                  <span className="text-base!">Viewing: </span>
                  <Select
                    as="square"
                    id="recruitRegion"
                    name="recruitRegion"
                    onChange={(e) => {
                      const findOption = REGION_OPTIONS.find((item) => item.value === e.target.value) ?? REGION_OPTIONS[0];
                      setRegion(findOption);
                    }}
                    options={REGION_OPTIONS}
                    value={region.value}
                    variant="secondary"
                  />
                </div>
                {/* Sort */}
                <Select
                  as="square"
                  id="recruitSort"
                  name="recruitSort"
                  onChange={(e) => {
                    const sortIndex = SORT.findIndex((item) => item.label === `Sort by ${e.target.value}`);
                    setSort(SORT[sortIndex]);
                    setSearchParams({ sort: sortIndex === 1 ? "new" : "old" });
                  }}
                  options={SORT.map((item) => ({
                    ...item,
                    name: item.label.replace("Sort by ", ""),
                    value: item.label.replace("Sort by ", ""),
                  }))}
                  value={sort.label.replace("Sort by ", "")}
                  variant="secondary"
                />
              </div>
            )}
          </section>
          {/* Articles */}
          <section className="pc:col-start-3 pc:col-span-10 col-span-8 flex min-h-screen flex-col gap-20">
            {recruits.map(({ node: recruit }: MetaobjectEdge) => (
              <article className="pc:tnt-gap-x pc:gap-y-0 pc:grid pc:grid-cols-10" key={recruit.handle}>
                <div className="pc:col-span-4 pc:gap-5 flex flex-col gap-4">
                  <div className="flex">
                    <span className="text-title flex! flex-1 flex-col whitespace-pre-line">
                      {getValue(recruit, "title")?.value?.replace(/\\n/g, "\n")}
                    </span>
                    {isPC ? (
                      <Button as="square" className="w-fit" onClick={() => { setSelectedHandle(recruit.handle); }} variant="secondary">
                        詳細を見る
                      </Button>
                    ) : null}
                  </div>
                  <div className="pc:tnt-gap-x pc:grid pc:grid-cols-4">
                    {isPC ? <span className="pc:col-span-1">Workplace</span> : null}
                    <span className="pc:col-span-3 pc:w-fit w-full leading-[130%]">{getValue(recruit, "location")?.value}</span>
                  </div>
                  {!isPC && (
                    <Button
                      as="square"
                      className="flex w-full justify-center"
                      onClick={() => { setSelectedHandle(recruit.handle); }}
                      variant="secondary"
                    >
                      詳細を見る
                    </Button>
                  )}
                </div>
              </article>
            ))}
          </section>
        </section>
      )}

      {selectedHandle ? (
        <RecruitLayout handleCancel={() => { setSelectedHandle(undefined); }}>
          <RecruitDetailContent handle={selectedHandle} />
        </RecruitLayout>
      ) : null}
    </>
  );
}
